import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Label } from '@givebutter/primitives/label';
import { faList } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getWebhookActivities } from 'api/webhooks';
import { Snippet } from 'components/snippet';
import { SnippetLanguage } from 'components/snippet/SnippetLanguage';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { EmptyData } from 'components/util';
import { useIsPending } from 'customHooks/useIsPending';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { webhookQueryKeys } from 'queries/webhook-query-keys';
import { WebhookActivity } from 'types/domain';

const columnHelper = createColumnHelper<WebhookActivity>();

const WebhookActivities = (props: { accountId: number; webhookId: string }) => {
    const { accountId, webhookId } = props;
    const { pagination, setPagination } = Table.useReactTableState();
    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: webhookQueryKeys.webhookActivities(accountId, webhookId, params),
        queryFn: () => getWebhookActivities(accountId, webhookId, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('event', {
                header: 'Event',
                cell: ({ getValue }) => {
                    return <>{getValue()}</>;
                },
            }),
            columnHelper.accessor('status_description', {
                header: 'Status',
                cell: info => {
                    const activity = info.row.original;
                    switch (activity.status) {
                        case 'failed':
                            return <Badge color="error">{activity.status_description}</Badge>;
                        case 'processing':
                            return <Badge>{activity.status_description}</Badge>;
                        case 'succeeded':
                            return <Badge color="success">{activity.status_description}</Badge>;
                        default:
                            return <EmptyData />;
                    }
                },
            }),
            columnHelper.accessor('created_at', {
                header: 'Created at',
                cell: ({ getValue }) =>
                    isoDateFromServer(getValue()).toLocaleString(
                        DateTime.DATETIME_SHORT_WITH_SECONDS,
                    ),
            }),
            columnHelper.accessor('updated_at', {
                header: 'Updated at',
                cell: info => {
                    const webhook = info.row.original;
                    return webhook.updated_at ? (
                        isoDateFromServer(webhook.updated_at).toLocaleString(
                            DateTime.DATETIME_SHORT_WITH_SECONDS,
                        )
                    ) : (
                        <EmptyData />
                    );
                },
            }),
        ],
        [],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            onRowClick: row => row.toggleExpanded(),
            renderExpandedRow: row => (
                <tr className="bg-gray-50">
                    <td colSpan={row.getVisibleCells().length}>
                        <div className="px-6 py-4">
                            <div className="flex w-full flex-col gap-4 text-sm">
                                <div className="flex flex-col gap-1">
                                    <Label>URL:</Label>
                                    {row.original.url}
                                </div>
                                <div className="flex flex-col gap-1">
                                    <Label>Request:</Label>
                                    <Snippet
                                        text={JSON.stringify(row.original.payload)}
                                        copyProps={{
                                            label: 'Copy',
                                        }}
                                        language={SnippetLanguage.JSON}
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <Label>Response:</Label>
                                    <Snippet
                                        text={JSON.stringify(row.original.response)}
                                        copyProps={{
                                            label: 'Copy',
                                        }}
                                        language={SnippetLanguage.JSON}
                                    />
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            ),
        },
    });

    useSyncPaginatedData(table, data);

    return (
        <div className="flex flex-col gap-2">
            <div className="font-medium">Activity</div>
            <Table.Root isLoading={isPending} variant={'bordered'} table={table}>
                <div className={'flex flex-col gap-4'}>
                    <Box className={'overflow-x-auto'}>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon>
                                <FontAwesomeIcon icon={faList} />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>No webhook activity found</Table.EmptyTitle>
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </div>
            </Table.Root>
        </div>
    );
};

export { WebhookActivities };
